import { mutate } from "swr";
import { apiClient, apiMutate } from "~/utils/apiClient";

const { VITE_CONVERSATIONS_API } = import.meta.env;
const SKILLS_API_PREFIX = `${VITE_CONVERSATIONS_API}/skills`;

/**
 * Invalidate every SWR key under the conversations skills surface — the
 * multi-scope picker (`/skills?...`), the per-scope attached lists
 * (`/agents/{id}/skills`, `/datasets/{id}/skills`), and any by-id reads.
 *
 * Called after mutations that change skill attachment in one surface so the
 * other surfaces don't sit on stale caches. Slightly broader than strictly
 * needed but cheap, since the affected pages aren't usually open at once.
 */
export function invalidateAllSkillLists(): Promise<unknown> {
  return mutate(
    (key) =>
      typeof key === "string" && key.startsWith(VITE_CONVERSATIONS_API) && key.includes("/skills"),
    undefined,
    { revalidate: true }
  );
}

export const SkillScope = {
  SYSTEM: "SYSTEM",
  USER: "USER",
  AGENT: "AGENT",
  DATASET: "DATASET",
  PROJECT: "PROJECT",
  CUSTOMER: "CUSTOMER",
} as const;
export type SkillScope = (typeof SkillScope)[keyof typeof SkillScope];

export interface PartialSkill {
  id: string;
  name: string;
  description: string;
  scope: SkillScope;
  scope_id: string | null;
  enabled: boolean;
  created_by: string | null;
  updated_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface Skill extends PartialSkill {
  content: string;
}

export interface SkillPageMetadata {
  limit: number;
  offset: number;
  total_count: number;
  has_more: boolean;
}

export interface SkillPage<T = PartialSkill> {
  items: T[];
  metadata: SkillPageMetadata;
}

export interface SkillFields {
  name: string;
  description: string;
  content: string;
}

export interface SkillUpdateInput {
  name?: string;
  description?: string;
  content?: string;
  enabled?: boolean;
}

export const SKILL_NAME_MAX_LENGTH = 255;
export const SKILL_NAME_MIN_LENGTH = 3;
export const SKILL_DESCRIPTION_MAX_LENGTH = 2048;
export const SKILL_CONTENT_MAX_LENGTH = 100000;
export const SKILL_NAME_PATTERN = /^[a-z][a-z0-9-]*$/;

// Shared cap for skill list endpoints (library, agent-attached, dataset-attached).
// The API supports pagination; we deliberately fetch a single page sized to the
// expected upper bound of any one workspace and surface no "load more" UI yet.
export const SKILL_LIST_LIMIT = 100;

export const DEFAULT_SKILL_CONTENT = `# my-skill

Describe when this skill applies and the steps the agent should follow.

## When to use it

- Trigger condition 1
- Trigger condition 2

## How to apply

1. **First step**: …
2. **Second step**: …
3. **Third step**: …
`;

export const skillsListPath = (params: Record<string, string | number | boolean>) => {
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(params).map(([k, v]) => [k, String(v)]))
  );
  return `${SKILLS_API_PREFIX}/?${qs.toString()}`;
};

export const skillPath = (skillId: string) => `${SKILLS_API_PREFIX}/${skillId}`;

export const agentSkillsListPath = (
  agentId: string,
  params: Record<string, string | number | boolean> = {}
) => {
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(params).map(([k, v]) => [k, String(v)]))
  );
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return `${VITE_CONVERSATIONS_API}/agents/${agentId}/skills${suffix}`;
};

export const agentSkillPath = (agentId: string, skillId: string) =>
  `${VITE_CONVERSATIONS_API}/agents/${agentId}/skills/${skillId}`;

// SYSTEM-scope skills are intentionally excluded from the multi-scope path
// and must be requested explicitly. We pull them on every conversation as a
// stable baseline.
export const systemSkillsPath = (): string =>
  `${SKILLS_API_PREFIX}/?scope=SYSTEM&unique=true&limit=${SKILL_LIST_LIMIT}`;

// Multi-scope: returns the union of AGENT-, DATASET-, and USER-scope skills
// the caller can see, deduped by name when `unique=true`. SYSTEM is never
// returned here. Returns `null` when there's no context to filter on so SWR
// keys can skip the fetch entirely.
export const contextSkillsPath = (args: {
  agent_id?: string | null;
  dataset_ids?: readonly string[] | null;
  user_id?: string | null;
}): string | null => {
  const params = new URLSearchParams();
  if (args.agent_id) params.append("agent_id", args.agent_id);
  if (args.dataset_ids?.length) {
    for (const id of args.dataset_ids) params.append("dataset_ids", id);
  }
  if (args.user_id) params.append("user_id", args.user_id);
  if ([...params.keys()].length === 0) return null;
  params.set("unique", "true");
  params.set("limit", String(SKILL_LIST_LIMIT));
  return `${SKILLS_API_PREFIX}/?${params.toString()}`;
};

export const datasetSkillsListPath = (
  datasetId: string,
  params: Record<string, string | number | boolean> = {}
) => {
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(params).map(([k, v]) => [k, String(v)]))
  );
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return `${VITE_CONVERSATIONS_API}/datasets/${datasetId}/skills${suffix}`;
};

export const datasetSkillPath = (datasetId: string, skillId: string) =>
  `${VITE_CONVERSATIONS_API}/datasets/${datasetId}/skills/${skillId}`;

export async function getSkill(skillId: string): Promise<Skill> {
  return apiClient<Skill>(skillPath(skillId));
}

export async function updateSkill(skillId: string, body: SkillUpdateInput): Promise<Skill> {
  return apiMutate<Skill>(skillPath(skillId), "PATCH", body);
}

export async function createUserSkill(fields: SkillFields): Promise<Skill> {
  return apiMutate<Skill>(`${SKILLS_API_PREFIX}/`, "POST", { ...fields, scope: "USER" });
}

export async function deleteUserSkill(skillId: string): Promise<void> {
  await apiMutate(skillPath(skillId), "DELETE", undefined, { expects204: true });
}

export async function attachAgentSkillFromFields(
  agentId: string,
  fields: SkillFields
): Promise<Skill> {
  return apiMutate<Skill>(agentSkillsListPath(agentId), "POST", { fields });
}

export async function attachAgentSkillFromId(agentId: string, skillId: string): Promise<Skill> {
  return apiMutate<Skill>(agentSkillsListPath(agentId), "POST", { skill_id: skillId });
}

export async function detachAgentSkill(agentId: string, skillId: string): Promise<void> {
  await apiMutate(agentSkillPath(agentId, skillId), "DELETE", undefined, { expects204: true });
}

export async function attachDatasetSkillFromFields(
  datasetId: string,
  fields: SkillFields
): Promise<Skill> {
  return apiMutate<Skill>(datasetSkillsListPath(datasetId), "POST", { fields });
}

export async function attachDatasetSkillFromId(datasetId: string, skillId: string): Promise<Skill> {
  return apiMutate<Skill>(datasetSkillsListPath(datasetId), "POST", { skill_id: skillId });
}

export async function detachDatasetSkill(datasetId: string, skillId: string): Promise<void> {
  await apiMutate(datasetSkillPath(datasetId, skillId), "DELETE", undefined, { expects204: true });
}
